import { Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import {
  defaultContestant,
  nextContestant,
  type Contestant,
  type EffortLevel,
  type ProductionModels,
  type ProviderId,
  type ProviderInfo,
} from '@/lib/model-lab'
import { ModelPicker } from './ModelPicker'

const DEFAULT = 'default'

interface Props {
  readonly providers: readonly ProviderInfo[]
  readonly contestants: readonly Contestant[]
  readonly productionModels?: ProductionModels
  readonly onChange: (next: Contestant[]) => void
}

export function ContestantEditor({ providers, contestants, productionModels, onChange }: Props) {
  const byId = new Map(providers.map((p) => [p.id, p]))

  const update = (index: number, patch: Partial<Contestant>) =>
    onChange(contestants.map((c, i) => (i === index ? { ...c, ...patch } : c)))

  const remove = (index: number) => onChange(contestants.filter((_, i) => i !== index))

  const next = nextContestant(providers, contestants)
  const add = () => {
    if (next) onChange([...contestants, next])
  }

  return (
    <div className="space-y-2">
      {contestants.map((c, index) => {
        const provider = byId.get(c.provider)
        return (
          <div key={index} className="grid grid-cols-[150px_1fr_110px_110px_auto] items-end gap-2 rounded-md border p-2">
            <div className="space-y-1">
              <Label className="text-xs">Provider</Label>
              <Select
                value={c.provider}
                onValueChange={(value) => {
                  const p = byId.get(value as ProviderId)
                  if (p) update(index, defaultContestant(p))
                }}
              >
                <SelectTrigger className="h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {providers.map((p) => (
                    <SelectItem key={p.id} value={p.id} disabled={!p.configured}>
                      {p.label}
                      {!p.configured && ` (set ${p.keyEnvVar})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label className="text-xs">Model</Label>
              <ModelPicker
                key={`${index}:${c.provider}`}
                provider={provider}
                model={c.model}
                productionModels={productionModels}
                onChange={(model) => update(index, { model })}
              />
            </div>

            <div className="space-y-1">
              <Label className="text-xs">Effort</Label>
              <Select
                value={c.effort ?? DEFAULT}
                onValueChange={(value) => update(index, { effort: value === DEFAULT ? undefined : (value as EffortLevel) })}
              >
                <SelectTrigger className="h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={DEFAULT}>Default</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label className="text-xs">Thinking</Label>
              <Select
                value={c.thinking === undefined ? DEFAULT : c.thinking ? 'on' : 'off'}
                disabled={!provider?.supportsThinkingToggle}
                onValueChange={(value) => update(index, { thinking: value === DEFAULT ? undefined : value === 'on' })}
              >
                <SelectTrigger className="h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={DEFAULT}>Default</SelectItem>
                  <SelectItem value="on">On</SelectItem>
                  <SelectItem value="off">Off</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-3 pb-1">
              {c.provider !== 'anthropic' && (
                <label className="flex items-center gap-1.5 text-xs" title="Ask for response_format: json_object on JSON tasks">
                  <Switch checked={c.jsonMode} onCheckedChange={(checked) => update(index, { jsonMode: checked })} />
                  JSON
                </label>
              )}
              <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => remove(index)} aria-label="Remove model">
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )
      })}

      <Button type="button" variant="outline" size="sm" onClick={add} disabled={!next}>
        <Plus className="mr-1 h-4 w-4" /> Add model
      </Button>
    </div>
  )
}
